import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ArrowLeft, Users2, UserCheck } from "lucide-react";
import { PriceLabel, PriceBreakdown } from "@/components/PriceIVA";
import MultiServiceFlow from "@/pages/MultiServiceFlow";

const DEFAULT_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "15:00", "15:30", "16:00",
  "16:30", "17:00", "17:30",
];

export default function BookAppointment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [services, setServices] = useState([]);
  const [multiMode, setMultiMode] = useState(false);
  const [step, setStep] = useState(1);
  const [serviceId, setServiceId] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [availability, setAvailability] = useState({ slots: DEFAULT_SLOTS, all_slots: DEFAULT_SLOTS, occupied: [] });
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [client, setClient] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const [credit, setCredit] = useState({ total: 0, count: 0 });

  useEffect(() => {
    const email = (client.email || "").trim().toLowerCase();
    if (!slug || !email || !/^\S+@\S+\.\S+$/.test(email)) { setCredit({ total: 0, count: 0 }); return; }
    const handle = setTimeout(() => {
      api.get(`/t/${slug}/credits`, { params: { email } })
        .then((r) => setCredit({ total: r.data.total || 0, count: r.data.count || 0 }))
        .catch(() => setCredit({ total: 0, count: 0 }));
    }, 400);
    return () => clearTimeout(handle);
  }, [client.email, slug]);

  useEffect(() => {
    if (!slug) return;
    api.get(`/t/${slug}/services`)
      .then((r) => setServices(r.data))
      .catch(() => toast.error("Negocio no encontrado o sin servicios"));
  }, [slug]);

  useEffect(() => {
    if (user) setClient((c) => ({ ...c, name: c.name || user.name, email: c.email || user.email, phone: c.phone || user.phone }));
  }, [user]);

  useEffect(() => {
    if (!serviceId || !slug) { setStaffList([]); return; }
    api.get(`/t/${slug}/staff/by-service/${serviceId}`).then((r) => setStaffList(r.data)).catch(() => setStaffList([]));
  }, [serviceId, slug]);

  useEffect(() => {
    if (!staffId || !date || !slug) {
      setAvailability({ slots: DEFAULT_SLOTS, all_slots: DEFAULT_SLOTS, occupied: [] });
      return;
    }
    setLoadingAvail(true);
    const d = format(date, "yyyy-MM-dd");
    api.get(`/t/${slug}/staff/${staffId}/availability`, { params: { date: d, service_id: serviceId } })
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability({ slots: [], all_slots: [], occupied: [] }))
      .finally(() => setLoadingAvail(false));
  }, [staffId, date, serviceId, slug]);

  const selectedService = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  const selectedStaff = useMemo(() => staffList.find((s) => s.id === staffId), [staffList, staffId]);

  async function submit() {
    if (!serviceId || !date || !time) return toast.error("Completa todos los datos");
    setLoading(true);
    try {
      const payload = {
        service_id: serviceId,
        staff_id: staffId || null,
        date: format(date, "yyyy-MM-dd"),
        time,
        client_name: client.name,
        client_email: client.email,
        client_phone: client.phone || "",
        notes: client.notes || "",
      };

      if (selectedService?.requires_payment) {
        const { data } = await api.post(`/t/${slug}/payments/checkout/session`, {
          ...payload,
          origin_url: window.location.origin,
          user_id: user?.id || null,
        });
        if (data?.url) { window.location.href = data.url; return; }
        throw new Error("No se pudo iniciar el pago");
      }

      const endpoint = `/t/${slug}/appointments/public`;
      const { data } = await api.post(endpoint, payload);
      setCreated(data);
      setStep(5);
      toast.success("Cita agendada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al agendar");
    } finally {
      setLoading(false);
    }
  }

  const stepNum = step === 5 ? 4 : step;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-slate-200 glass-nav sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="book-back-home">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Link>
          <p className="overline" data-testid="book-step-indicator">Paso {Math.min(stepNum, 4)} de 4</p>
          <div className="w-16" />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-12 sm:py-16">
        {step !== 5 && (
          <div className="mb-10">
            <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight">Agenda tu cita</h1>
            <p className="text-slate-600 mt-3 max-w-xl">Servicio, profesional, fecha y hora — en menos de 1 minuto.</p>
          </div>
        )}

        {step === 1 && !multiMode && services.some((s) => s.overlap_enabled) && (
          <div className="mb-6 flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
            <div>
              <Label htmlFor="multi-switch" className="cursor-pointer text-sm font-medium">Agendar varios servicios en una visita</Label>
              <p className="text-xs text-slate-500 mt-1">Agenda 2 o más servicios compatibles en un solo bloque, minimizando tu tiempo de espera.</p>
            </div>
            <Switch id="multi-switch" checked={multiMode} onCheckedChange={setMultiMode} data-testid="multi-mode-switch" />
          </div>
        )}

        {multiMode && step !== 5 ? (
          <MultiServiceFlow services={services} user={user} onBackToSingle={() => setMultiMode(false)} />
        ) : (
        <>

        {/* Step 1: Service */}
        {step === 1 && (
          <Card className="border-slate-200">
            <CardContent className="p-6 sm:p-8">
              <p className="overline">01 · Servicio</p>
              <h2 className="font-display text-2xl mt-2 mb-6">¿Qué servicio necesitas?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {services.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setServiceId(s.id); setTimeout(() => setStep(2), 250); }}
                    className={`text-left rounded-lg border p-5 card-hover transition-all duration-200 ${serviceId === s.id ? "border-primary bg-secondary ring-2 ring-primary/30 scale-[1.01]" : "border-slate-200 bg-white"}`}
                    data-testid={`service-option-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-display text-lg">{s.name}</p>
                        <p className="text-sm text-slate-600 mt-1">{s.description}</p>
                      </div>
                      {serviceId === s.id && <Check className="h-5 w-5 text-primary" />}
                    </div>
                    <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
                      <span>{s.duration_min} min</span>
                      <PriceLabel price={s.price} ivaIncluded={s.iva_included !== false} size="sm" />
                      {s.requires_payment && (
                        <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))]">
                          Depósito {s.deposit_pct || 100}%
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Staff */}
        {step === 2 && (
          <Card className="border-slate-200">
            <CardContent className="p-6 sm:p-8">
              <p className="overline">02 · Profesional</p>
              <h2 className="font-display text-2xl mt-2 mb-6">¿Con quién?</h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <button
                  onClick={() => { setStaffId(""); setTimeout(() => setStep(3), 250); }}
                  className={`text-left rounded-lg border p-5 card-hover transition-all duration-200 ${staffId === "" ? "border-primary bg-secondary ring-2 ring-primary/30 scale-[1.01]" : "border-slate-200 bg-white"}`}
                  data-testid="staff-option-any"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="h-12 w-12 rounded-full bg-slate-100 grid place-items-center"><Users2 className="h-5 w-5 text-slate-500" /></div>
                    {staffId === "" && <Check className="h-5 w-5 text-primary" />}
                  </div>
                  <p className="font-display text-base mt-4">Sin preferencia</p>
                  <p className="text-xs text-slate-500 mt-1">Te asignamos al profesional con más disponibilidad.</p>
                </button>

                {staffList.length === 0 && (
                  <div className="sm:col-span-2 lg:col-span-2 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="no-staff-warning">
                    Aún no hay profesionales asignados a este servicio. Puedes continuar "Sin preferencia" y el equipo asignará uno.
                  </div>
                )}

                {staffList.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setStaffId(s.id); setTimeout(() => setStep(3), 250); }}
                    className={`text-left rounded-lg border p-5 card-hover transition-all duration-200 ${staffId === s.id ? "border-primary bg-secondary ring-2 ring-primary/30 scale-[1.01]" : "border-slate-200 bg-white"}`}
                    data-testid={`staff-option-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      {s.photo_url ? (
                        <img src={s.photo_url} alt="" className="h-12 w-12 rounded-full object-cover" />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-secondary text-secondary-foreground grid place-items-center font-display text-lg">{s.name?.[0] || "·"}</div>
                      )}
                      {staffId === s.id && <Check className="h-5 w-5 text-primary" />}
                    </div>
                    <p className="font-display text-base mt-4">{s.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{s.title}</p>
                  </button>
                ))}
              </div>

              <div className="mt-8 flex justify-start">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="book-step2-back">Atrás</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Date + Time */}
        {step === 3 && (
          <Card className="border-slate-200">
            <CardContent className="p-6 sm:p-8">
              <p className="overline">03 · Fecha y hora</p>
              <h2 className="font-display text-2xl mt-2 mb-6">¿Cuándo te conviene?</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <Label className="mb-2 block">Selecciona fecha</Label>
                  <div className="rounded-lg border border-slate-200 bg-white inline-block">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setTime(""); }}
                      locale={es}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      data-testid="book-calendar"
                    />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Selecciona hora</Label>
                  {staffId && (
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><UserCheck className="h-3 w-3" /> Mostrando solo horarios libres para {selectedStaff?.name}</p>
                  )}
                  {loadingAvail ? (
                    <p className="text-sm text-slate-500">Cargando…</p>
                  ) : (staffId && availability.all_slots.length === 0) ? (
                    <p className="text-sm text-slate-500" data-testid="no-slots-warning">El profesional no atiende ese día.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {(staffId ? availability.all_slots : DEFAULT_SLOTS).map((t) => {
                        const isOccupied = staffId ? availability.occupied.includes(t) : false;
                        const isSelected = time === t;
                        return (
                          <button
                            key={t}
                            disabled={isOccupied}
                            onClick={() => setTime(t)}
                            className={`rounded-md border px-3 py-2 text-sm transition ${
                              isSelected ? "bg-primary text-primary-foreground border-primary" :
                              isOccupied ? "bg-slate-50 text-slate-400 border-slate-200 line-through cursor-not-allowed" :
                              "bg-white border-slate-200 hover:border-slate-400"
                            }`}
                            data-testid={`time-slot-${t}`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedService && date && time && (
                    <div className="mt-6 rounded-md bg-slate-50 border border-slate-200 p-4 text-sm">
                      <p className="overline">Resumen</p>
                      <p className="mt-2 font-display text-base">{selectedService.name}</p>
                      <p className="text-slate-600 mt-1">{format(date, "EEEE d 'de' MMMM, yyyy", { locale: es })} · {time}</p>
                      {selectedStaff && <p className="text-slate-600">Con {selectedStaff.name}</p>}
                      <p className="text-slate-600">Total: ${selectedService.price.toFixed(2)}</p>
                      {selectedService.requires_payment && (
                        <p className="mt-2 text-[hsl(var(--accent))] font-semibold text-xs uppercase tracking-wider">
                          Depósito: ${(selectedService.price * (selectedService.deposit_pct || 100) / 100).toFixed(2)} ({selectedService.deposit_pct || 100}%)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-8 flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="book-step3-back">Atrás</Button>
                <Button disabled={!date || !time} onClick={() => setStep(4)} data-testid="book-step3-next">Continuar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Contact */}
        {step === 4 && (
          <Card className="border-slate-200">
            <CardContent className="p-6 sm:p-8">
              <p className="overline">04 · Tus datos</p>
              <h2 className="font-display text-2xl mt-2 mb-6">Casi listo</h2>

              {selectedService?.requires_payment && (
                <div className="mb-6 rounded-lg border border-[hsl(var(--accent))]/40 bg-secondary p-4 text-sm" data-testid="book-deposit-summary">
                  <p className="overline" style={{ color: "#1E3A8A" }}>Pre-pago requerido</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-slate-600 text-xs">Total servicio</p>
                      <p className="font-display text-lg">${selectedService.price.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-600 text-xs">Depósito ({selectedService.deposit_pct || 100}%)</p>
                      <p className="font-display text-lg" style={{ color: "#1E3A8A" }}>
                        ${(selectedService.price * (selectedService.deposit_pct || 100) / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">Te redirigiremos a Stripe Checkout (pago seguro). El restante se cobrará en la cita.</p>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cname">Nombre completo</Label>
                  <Input id="cname" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} required data-testid="book-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cemail">Correo</Label>
                  <Input id="cemail" type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} required data-testid="book-email-input" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="cphone">Teléfono (opcional)</Label>
                  <Input id="cphone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} data-testid="book-phone-input" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="cnotes">Notas (opcional)</Label>
                  <Textarea id="cnotes" value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} data-testid="book-notes-input" />
                </div>
                {credit.total > 0 && (
                  <div className="sm:col-span-2 rounded-xl p-4 text-white flex items-center gap-3" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)" }} data-testid="book-credit-banner">
                    <div className="h-10 w-10 rounded-full bg-white/15 grid place-items-center shrink-0">
                      <span className="text-lg">💰</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] tracking-[0.18em] uppercase text-white/70">Saldo a favor detectado</p>
                      <p className="font-display text-lg leading-tight mt-0.5">Tienes <strong>${credit.total.toFixed(2)} MXN</strong> disponible</p>
                      <p className="text-xs opacity-90 mt-1">Menciona este saldo al negocio al momento de tu cita para aplicarlo a tu servicio.</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-8 flex justify-between">
                <Button variant="outline" onClick={() => setStep(3)} data-testid="book-step4-back">Atrás</Button>
                <Button onClick={submit} disabled={loading || !client.name || !client.email} data-testid="book-confirm-btn">
                  {loading
                    ? (selectedService?.requires_payment ? "Redirigiendo a pago…" : "Agendando…")
                    : (selectedService?.requires_payment ? `Pagar depósito $${(selectedService.price * (selectedService.deposit_pct || 100) / 100).toFixed(2)}` : "Confirmar cita")
                  }
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Success */}
        {step === 5 && created && (
          <Card className="border-slate-200" data-testid="book-success-card">
            <CardContent className="p-8 sm:p-12 text-center">
              <div className="mx-auto h-14 w-14 rounded-full bg-secondary text-secondary-foreground grid place-items-center">
                <Check className="h-6 w-6" />
              </div>
              <h2 className="font-display text-3xl font-light mt-6">¡Cita confirmada!</h2>
              <p className="text-slate-600 mt-3">Te enviamos un correo a <span className="font-semibold">{created.client_email}</span></p>
              <div className="mt-8 inline-block rounded-lg border border-slate-200 bg-white p-6 text-left min-w-[280px]">
                <p className="overline">Detalle</p>
                <p className="font-display text-lg mt-2">{created.service_name}</p>
                {created.staff_name && <p className="text-sm text-slate-600 mt-1">Con {created.staff_name}</p>}
                <p className="text-sm text-slate-600">{created.date} · {created.time}</p>
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <PriceBreakdown total={created.service_price} ivaIncluded={selectedService?.iva_included !== false} label="Total del servicio" testId="book-confirm-breakdown" />
                </div>
              </div>
              <div className="mt-8 flex justify-center gap-3">
                <Button variant="outline" onClick={() => navigate("/")} data-testid="book-success-home">Ir al inicio</Button>
                {!user && <Button onClick={() => navigate("/register")} data-testid="book-success-register">Crear cuenta</Button>}
                {user && <Button onClick={() => navigate("/dashboard")} data-testid="book-success-dashboard">Mi cuenta</Button>}
              </div>
            </CardContent>
          </Card>
        )}
        </>
        )}
      </main>
    </div>
  );
}
